import React, { useMemo, useState } from 'react'
import { Box, Button, Checkbox, FormControlLabel, IconButton, InputBase, Stack, Typography } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'

import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

export interface FilterOption {
    key: string
    label: string
    count: number
    selected: boolean
}

interface PackagesFilterPanelProps {
    title: string
    clearAllLabel: string
    onClearAll: () => void
    hasActiveFilters: boolean
    destinationTitle: string
    destinationSearchPlaceholder: string
    cityOptions: FilterOption[]
    onToggleCity: (key: string) => void
    /** "Show all {count} destinations" / "Show less" — the list is long enough to need folding. */
    showAllLabel: string
    showLessLabel: string
    showMoreLabel: string
    monthTitle: string
    monthOptions: FilterOption[]
    onToggleMonth: (key: string) => void
    occasionTitle: string
    occasionOptions: FilterOption[]
    onToggleOccasion: (key: string) => void
    travelStyleTitle: string
    travelStyleOptions: FilterOption[]
    onToggleTravelStyle: (key: string) => void
    noMatchesLabel: string
    clearSearchLabel: string
    isMobile: boolean
}

/** Vertical gap between two filter rows, on the theme's spacing scale (1 unit = 8px). */
const FILTER_ROW_GAP = 0.875

/** Enough destinations to show the filter is worth using, few enough not to push the grid down. */
const CITY_PREVIEW_COUNT = 6

/** Travel styles shown before the reader asks for the rest. */
const TRAVEL_STYLE_PREVIEW_COUNT = 5

const FilterGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Stack spacing={1.25}>
        <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700, color: 'textColors.primary' }}>
            {title}
        </Typography>
        {children}
    </Stack>
)

const FilterCheckbox: React.FC<{ option: FilterOption; onToggle: (key: string) => void }> = ({
    option,
    onToggle
}) => (
    <FormControlLabel
        control={
            <Checkbox
                checked={option.selected}
                onChange={() => onToggle(option.key)}
                size="small"
                sx={{ py: 0 }}
            />
        }
        label={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Typography sx={{ fontSize: '0.938rem', color: 'textColors.primary' }}>{option.label}</Typography>
                <Typography sx={{ fontSize: '0.813rem', color: 'textColors.subheading' }}>{option.count}</Typography>
            </Box>
        }
        sx={{
            m: 0,
            width: '100%',
            alignItems: 'flex-start',
            '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0, ml: 0.5, lineHeight: 1.45 }
        }}
    />
)

const PackagesFilterPanel: React.FC<PackagesFilterPanelProps> = ({
    title,
    clearAllLabel,
    onClearAll,
    hasActiveFilters,
    destinationTitle,
    destinationSearchPlaceholder,
    cityOptions,
    onToggleCity,
    showAllLabel,
    showLessLabel,
    showMoreLabel,
    monthTitle,
    monthOptions,
    onToggleMonth,
    occasionTitle,
    occasionOptions,
    onToggleOccasion,
    travelStyleTitle,
    travelStyleOptions,
    onToggleTravelStyle,
    noMatchesLabel,
    clearSearchLabel,
    isMobile
}) => {
    const [searchFocused, setSearchFocused] = useState(false)
    const [cityQuery, setCityQuery] = useState('')
    const [showAllCities, setShowAllCities] = useState(false)
    const [showAllStyles, setShowAllStyles] = useState(false)

    const matchingCities = useMemo(() => {
        const needle = cityQuery.trim().toLowerCase()
        if (!needle) return cityOptions
        return cityOptions.filter(option => option.label.toLowerCase().includes(needle))
    }, [cityOptions, cityQuery])

    /*
     * A selected city stays on screen even when it falls outside the preview, or unticking it would
     * mean expanding the list to find it again.
     */
    const visibleCities = useMemo(() => {
        if (showAllCities || cityQuery.trim()) return matchingCities
        const preview = matchingCities.slice(0, CITY_PREVIEW_COUNT)
        const selectedBelow = matchingCities.slice(CITY_PREVIEW_COUNT).filter(option => option.selected)
        return [...preview, ...selectedBelow]
    }, [matchingCities, showAllCities, cityQuery])

    const canFold = !cityQuery.trim() && matchingCities.length > CITY_PREVIEW_COUNT

    const visibleStyles = useMemo(() => {
        if (showAllStyles) return travelStyleOptions
        const preview = travelStyleOptions.slice(0, TRAVEL_STYLE_PREVIEW_COUNT)
        const selectedBelow = travelStyleOptions.slice(TRAVEL_STYLE_PREVIEW_COUNT).filter(option => option.selected)
        return [...preview, ...selectedBelow]
    }, [travelStyleOptions, showAllStyles])

    const canFoldStyles = travelStyleOptions.length > TRAVEL_STYLE_PREVIEW_COUNT

    return (
        <Stack
            spacing={2.5}
            sx={{
                p: isMobile ? 0 : 2.5,
                borderRadius: HOLIDAYS_RADIUS.SURFACE,
                border: isMobile ? 'none' : '1px solid',
                borderColor: 'borders.default',
                bgcolor: 'background.paper'
            }}
        >
            {!isMobile && (
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography component="h2" sx={{ fontSize: '1.125rem', fontWeight: 700 }}>
                        {title}
                    </Typography>
                    {hasActiveFilters && (
                        <Button onClick={onClearAll} sx={{ fontSize: '0.813rem', fontWeight: 700, p: 0.5 }}>
                            {clearAllLabel}
                        </Button>
                    )}
                </Stack>
            )}

            <FilterGroup title={destinationTitle}>
                {/*
                 * Built from InputBase rather than a TextField: an outlined TextField draws MUI's
                 * own fieldset, whose default border and focus ring are black, and no amount of
                 * theming on this page reaches it. The border lives on the wrapper instead, on the
                 * same tokens as every other holidays surface.
                 */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.25,
                        py: 0.75,
                        borderRadius: HOLIDAYS_RADIUS.NESTED,
                        border: '1px solid',
                        borderColor: searchFocused ? 'primary.main' : 'borders.default',
                        bgcolor: 'background.paper',
                        transition: 'border-color 0.2s',
                        // Hover is the more specific rule, so it has to defer to focus itself —
                        // otherwise pointing at the field you are typing in greys its border out.
                        '&:hover': { borderColor: searchFocused ? 'primary.main' : 'borders.heavy' },
                        '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
                    }}
                >
                    <SearchRoundedIcon
                        sx={{
                            fontSize: 18,
                            flexShrink: 0,
                            color: searchFocused ? 'primary.main' : 'textColors.subheading'
                        }}
                    />
                    <InputBase
                        type="search"
                        value={cityQuery}
                        placeholder={destinationSearchPlaceholder}
                        onChange={event => setCityQuery(event.target.value)}
                        onFocus={() => setSearchFocused(true)}
                        onBlur={() => setSearchFocused(false)}
                        inputProps={{ 'aria-label': destinationSearchPlaceholder }}
                        sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem' }}
                    />
                    {cityQuery && (
                        <IconButton
                            aria-label={clearSearchLabel}
                            onClick={() => setCityQuery('')}
                            size="small"
                            sx={{ p: 0.25 }}
                        >
                            <CloseRoundedIcon sx={{ fontSize: 16, color: 'textColors.subheading' }} />
                        </IconButton>
                    )}
                </Box>

                {visibleCities.length ? (
                    <Stack spacing={FILTER_ROW_GAP}>
                        {visibleCities.map(option => (
                            <FilterCheckbox key={option.key} option={option} onToggle={onToggleCity} />
                        ))}
                    </Stack>
                ) : (
                    <Typography sx={{ fontSize: '0.875rem', color: 'textColors.subheading' }}>
                        {noMatchesLabel}
                    </Typography>
                )}

                {canFold && (
                    <Button
                        onClick={() => setShowAllCities(current => !current)}
                        sx={{ alignSelf: 'flex-start', p: 0.5, fontSize: '0.875rem', fontWeight: 700 }}
                    >
                        {showAllCities ? showLessLabel : showAllLabel}
                    </Button>
                )}
            </FilterGroup>

            {travelStyleOptions.length > 1 && (
                <>
                    <Box sx={{ height: '1px', bgcolor: 'borders.default' }} />

                    <FilterGroup title={travelStyleTitle}>
                        <Stack spacing={FILTER_ROW_GAP}>
                            {visibleStyles.map(option => (
                                <FilterCheckbox key={option.key} option={option} onToggle={onToggleTravelStyle} />
                            ))}
                        </Stack>

                        {canFoldStyles && (
                            <Button
                                onClick={() => setShowAllStyles(current => !current)}
                                sx={{ alignSelf: 'flex-start', p: 0.5, fontSize: '0.875rem', fontWeight: 700 }}
                            >
                                {showAllStyles ? showLessLabel : showMoreLabel}
                            </Button>
                        )}
                    </FilterGroup>
                </>
            )}

            <Box sx={{ height: '1px', bgcolor: 'borders.default' }} />

            <FilterGroup title={monthTitle}>
                <Stack spacing={FILTER_ROW_GAP}>
                    {monthOptions.map(option => (
                        <FilterCheckbox key={option.key} option={option} onToggle={onToggleMonth} />
                    ))}
                </Stack>
            </FilterGroup>

            {/* A lone occasion filters nothing, so the group only appears once there are two. */}
            {occasionOptions.length > 1 && (
                <>
                    <Box sx={{ height: '1px', bgcolor: 'borders.default' }} />

                    <FilterGroup title={occasionTitle}>
                        <Stack spacing={FILTER_ROW_GAP}>
                            {occasionOptions.map(option => (
                                <FilterCheckbox key={option.key} option={option} onToggle={onToggleOccasion} />
                            ))}
                        </Stack>
                    </FilterGroup>
                </>
            )}
        </Stack>
    )
}

export { PackagesFilterPanel }
export type { PackagesFilterPanelProps }
export default PackagesFilterPanel
